import moment from "moment";
import { array, boolean, number, object, string } from "yup";
import { ResCallback } from "../models";
import { StringUtils, formatBytes } from "../utils";
import { AvisConstants } from "./avisConstants";

//String values
export const simpleString = (res: ResCallback, min?: number, max?: number) => {
  let stringSchema = string();

  if (min) {
    stringSchema = stringSchema.min(
      min,
      res("validation_MinLengthRequired", "", min.toString()),
    );
  }
  if (max) {
    stringSchema = stringSchema.max(
      max,
      res("validation_MaxLengthExceeded", "", max.toString()),
    );
  }
  return stringSchema;
};
export const requiredString = (
  res: ResCallback,
  min?: number,
  max?: number,
) => {
  const stringSchema = simpleString(res, min, max);
  return stringSchema
    .required(res("validation_FieldIsRequired"))
    .matches(/^\s*\S[\s\S]*$/, res("validation_FieldIsRequired"));
};
export const stringTiming = (res: ResCallback) =>
  string().matches(
    /^(?:(?:([01]?\d|2[0-3]):)?([0-5]?\d):)?([0-5]?\d)$/,
    res("validation_timing_format"),
  );

//Number values
export const requiredNumber = (res: ResCallback) =>
  number().required(res("validation_FieldIsRequired"));
export const numberDecimal = (
  res: ResCallback,
  requiredIn?: boolean,
  nonNegative?: boolean,
  positiveOnly?: boolean,
) => {
  const yupNumber = number()
    .typeError(res("validation_NumberRequired"))
    .when([], {
      is: () => requiredIn,
      then: (schema) => schema.required(res("validation_FieldIsRequired")),
    })
    .when([], {
      is: () => nonNegative || positiveOnly,
      then: (schema) =>
        positiveOnly
          ? schema.moreThan(0, res("validation_PositiveNumberRequired"))
          : schema.min(0, res("validation_NonNegativeNumberRequired")),
    });

  return yupNumber;
};
export const numberInteger = (
  res: ResCallback,
  requiredIn?: boolean,
  nonNegative?: boolean,
  positiveOnly?: boolean,
) => {
  const yupInteger = numberDecimal(res, requiredIn, nonNegative, positiveOnly)
    .integer(res("validation_IntegerRequired"))
    .max(AvisConstants.max_int_value, res("validation_IntegerRequired"))
    .typeError(res("validation_IntegerRequired"));
  return yupInteger;
};

//DateTime & periods
export const requiredPeriodValidation = (
  res: ResCallback,
  periodFrom: Date | undefined,
  periodTo: Date | undefined,
) =>
  requiredString(res).test(
    "outside period",
    res("validation_DateOutsidePeriodBoundaries"),
    (v) => {
      const vDate = moment(v).toDate();
      const validBefore = periodFrom ? vDate >= periodFrom : true;
      const validAfter = periodTo ? vDate <= periodTo : true;

      return validBefore && validAfter;
    },
  );

//Other
export const requiredClassifierId = (res: ResCallback) =>
  number()
    .integer(res("validation_FieldIsRequired"))
    .min(1, res("validation_FieldIsRequired"))
    .required(res("validation_FieldIsRequired"));
export const requiredBoolean = (res: ResCallback) =>
  boolean().required(res("validation_FieldIsRequired"));

//File Collection

export const fileCollectionValidation = (
  res: ResCallback,
  uploadableFileTypes: string[] | undefined,
  unsupportedSymbols: string[],
  maxFileSize: number,
  allowBlankContent?: boolean,
) => {
  const allowedFileExtensions = uploadableFileTypes
    ? uploadableFileTypes.map((ext) => `.${ext}`)
    : [];

  return array()
    .of(
      object().shape({
        fileName: requiredString(res)
          .test(
            "unsupported-symbols",
            StringUtils.decodeHTMLEntities(
              res(
                "error_FileAttachments_BadSymbols",
                unsupportedSymbols.join(" "),
              ),
            ),
            (v) => {
              return (
                unsupportedSymbols.filter(
                  (symbol) => v && v.indexOf(symbol) > -1,
                ).length === 0
              );
            },
          )
          .test("double-symbols", (v, context) => {
            //Check if filename contains ".."
            const isValid = v?.split(/(\.\.)/).length === 1;

            return !isValid
              ? context.createError({
                  message: res("error_FileAttachments_DoubleSymbols", v),
                })
              : true;
          })
          .test(
            "unsupported type",
            res(
              "error_FileAttachments_BadFileType",
              allowedFileExtensions.join(" "),
            ),
            (v) => {
              const extensionValue = v.split(".").pop()?.toLowerCase() ?? "";
              return allowedFileExtensions.indexOf(`.${extensionValue}`) > -1;
            },
          ),
        base64Data: string().when([], {
          is: () => !allowBlankContent,
          then: (schema) =>
            schema.required(res("error_FileAttachments_EmptyFile")),
          otherwise: (schema) => schema.optional(),
        }),
      }),
    )
    .test(
      "uniqueFilenames",
      res("error_FileAttachments_DuplicateFilename"),
      (value) => {
        if (!value) return true;
        const uniqueItemsByFilename = value.filter(
          (obj, index) =>
            value.findIndex((item) => item.fileName === obj.fileName) === index,
        );

        return uniqueItemsByFilename.length === value.length;
      },
    )
    .when([], {
      is: true,
      then: (schema) =>
        schema.test(
          "Byte array exceeds max length",
          res("error_FileAttachments_FileTooLarge", formatBytes(maxFileSize)),
          (value) => {
            let contentLength = 0;
            if (!value) return;
            value.forEach((file) => {
              if (!file.base64Data) return;
              contentLength += atob(file.base64Data).length;
            });

            if (contentLength > maxFileSize) return false;

            return true;
          },
        ),
    });
};
